import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL as CONFIG_API_URL } from '../config';
import { Users, Loader2, Sparkles, MessageSquare, Activity, Target } from 'lucide-react';
import ReactECharts from 'echarts-for-react';

const API_URL = CONFIG_API_URL || 'http://43.135.176.56:5000/api';

export default function PersonaInsightsTab({ brandProjectId, taskId, filters, fetchMode, onDrawerOpen }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedBrands, setSelectedBrands] = useState([]);

  // Reset selected brands when project/brand changes so top competitors load freshly
  useEffect(() => {
    setSelectedBrands([]);
  }, [brandProjectId]);
  const [tagsCache, setTagsCache] = useState({});
  const [generatingTags, setGeneratingTags] = useState({});

  useEffect(() => {
    if (!brandProjectId || brandProjectId === 'all') {
      setLoading(false);
      setData(null);
      return;
    }
    
    const fetchData = async () => {
      try {
        setLoading(true);
        
        let startDate, endDate;
        if (filters) {
          if (filters.time === '最近 7 天') {
            const start = new Date(); start.setDate(start.getDate() - 7); startDate = start.toISOString();
          } else if (filters.time === '最近 30 天') {
            const start = new Date(); start.setDate(start.getDate() - 30); startDate = start.toISOString();
          } else if (filters.time === '最近 90 天') {
            const start = new Date(); start.setDate(start.getDate() - 90); startDate = start.toISOString();
          } else if (filters.time === '自定义时间' || filters.time === '自定义范围') {
            startDate = filters.customDate?.start ? new Date(filters.customDate.start).toISOString() : undefined;
          }

          if (filters.time === '最近 7 天' || filters.time === '最近 30 天' || filters.time === '最近 90 天') {
            endDate = new Date().toISOString();
          } else if (filters.time === '自定义时间' || filters.time === '自定义范围') {
            if (filters.customDate?.end) {
              const end = new Date(filters.customDate.end);
              end.setHours(23, 59, 59, 999);
              endDate = end.toISOString();
            }
          }
        }

        const res = await axios.get(`${API_URL}/persona-insights`, {
          params: { 
            brandProjectId,
            taskId,
            fetchMode,
            engines: filters?.engines?.includes('全部 AI 引擎') ? 'all' : filters?.engines?.join(','),
            stages: filters?.stages?.includes('全部阶段') ? 'all' : filters?.stages?.join(','),
            packages: filters?.packages?.includes('全部词包') ? 'all' : filters?.packages?.join(','),
            personas: filters?.personas?.includes('全部人设') ? 'all' : filters?.personas?.join(','),
            startDate,
            endDate
          }
        });
        if (res.data && res.data.success) {
          setData(res.data.data);
          if (res.data.data?.competitors?.competitors) {
              const compList = res.data.data.competitors.competitors;
              let topBrands = compList.slice(0, 6);
              const allBrands = compList.map(c => c.name || c.brand || c);
              setSelectedBrands(prev => {
                 if (prev.length === 0) return topBrands;
                 const hasOverlap = prev.some(b => allBrands.includes(b));
                 if (!hasOverlap && allBrands.length > 0) return topBrands;
                 return prev;
              });
          }
        }
      } catch (e) {
        console.error("Failed to fetch persona insights", e);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [brandProjectId, taskId, filters, fetchMode]);

  const handleGenerateTags = async (personaName) => {
    setGeneratingTags(prev => ({ ...prev, [personaName]: true }));
    try {
      const res = await axios.post(`${API_URL}/persona-insights/generate-tags`, {
        brandProjectId,
        personaName
      });
      if (res.data.success) {
        setTagsCache(prev => ({ ...prev, [personaName]: res.data.tags }));
      }
    } catch (e) {
      console.error(e);
      alert('生成标签失败，请重试');
    } finally {
      setGeneratingTags(prev => ({ ...prev, [personaName]: false }));
    }
  };

  // We should also pre-fetch the existing tags from the DB when loading the Insights 
  useEffect(() => {
    if (data && data.sentiments) {
      const prefetchTags = async () => {
        try {
          const res = await axios.get(`${API_URL}/persona-insights/tags`, {
            params: { brandProjectId }
          });
          if (res.data && res.data.success) {
            setTagsCache(res.data.tags);
          }
        } catch(e) {}
      }
      prefetchTags();
    }
  }, [data, brandProjectId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin mb-4 text-[#2996cf]" />
        <p>正在拉取人群差异圈层数据...</p>
      </div>
    );
  }

  if (!data || !data.sentiments || data.sentiments.length === 0) {
    if (!brandProjectId || brandProjectId === 'all') {
      return (
        <div className="py-20 text-center text-gray-500 bg-white dark:bg-slate-800 rounded-xl mt-4">
          请在上方筛选器中选择【具体的品牌项目】以查看人群差异圈层洞察。
        </div>
      );
    }
    return (
      <div className="py-20 text-center text-gray-500 bg-white dark:bg-slate-800 rounded-xl mt-4">
        该项目暂无人设相关数据。请配置人设进行分发。
      </div>
    );
  }

  // 1. Chart Options: Sentiment Distribution
  const sentimentPersonas = data.sentiments.map(s => s.persona);
  const posData = data.sentiments.map(s => s.positive);
  const neuData = data.sentiments.map(s => s.neutral);
  const conData = data.sentiments.map(s => s.controversial || 0);
  const negData = data.sentiments.map(s => s.negative);

  const isDark = document.documentElement.classList.contains('dark');
  const textColor = isDark ? '#cbd5e1' : '#1e293b';

  const sentimentOption = {
    tooltip: { 
      trigger: 'axis', 
      axisPointer: { type: 'shadow' }, 
      formatter: (params) => {
        const dataIndex = params[0].dataIndex;
        const total = data.sentiments[dataIndex].totalCount || 0;
        return `<div class="font-sans">
          <div class="font-bold text-sm mb-1">${params[0].name} <span class="text-xs text-gray-500 font-normal">(样本量: ${total})</span></div>
          <div class="text-sm">正向评价: <span class="font-bold">${params[0].value}%</span></div>
          <div class="text-sm">中性评价: <span class="font-bold">${params[1].value}%</span></div>
          <div class="text-sm">争议评价: <span class="font-bold">${params[2].value}%</span></div>
          <div class="text-sm">负向评价: <span class="font-bold">${params[3].value}%</span></div>
        </div>`;
      } 
    },
    legend: { data: ['正向评价 (推荐/好评)', '中性评价 (客观陈述)', '争议评价 (优缺参半/多面)', '负向评价 (劝退/吐槽)'], bottom: 0, textStyle: { color: textColor } },
    grid: { left: '3%', right: '4%', bottom: '15%', top: '5%', containLabel: true },
    xAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%', color: textColor }, splitLine: { lineStyle: { color: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' } } },
    yAxis: { type: 'category', data: sentimentPersonas, axisLabel: { color: textColor, fontWeight: 'bold' } },
    series: [
      { name: '正向评价 (推荐/好评)', type: 'bar', stack: 'total', itemStyle: { color: '#397AAA' }, barWidth: 28, data: posData },
      { name: '中性评价 (客观陈述)', type: 'bar', stack: 'total', itemStyle: { color: '#94a3b8' }, data: neuData },
      { name: '争议评价 (优缺参半/多面)', type: 'bar', stack: 'total', itemStyle: { color: '#eabc6b' }, data: conData },
      { name: '负向评价 (劝退/吐槽)', type: 'bar', stack: 'total', itemStyle: { color: '#e56d4e', borderRadius: [0,4,4,0] }, data: negData }
    ]
  };

  // 2. Chart Options: Competitor Heatmap
  const compPersonas = data.competitors.personas;
  const originalCompBrands = data.competitors.competitors || [];
  const compBrands = originalCompBrands.filter(b => selectedBrands.includes(b));
  
  const heatData = data.competitors.matrix
    .filter(item => selectedBrands.includes(item.competitor))
    .map(item => [
       compBrands.indexOf(item.competitor),
       compPersonas.indexOf(item.persona),
       Number(item.rate),
       item.count,
       item.total
    ]);

  const heatmapOption = {
    tooltip: { 
      position: 'top', 
      formatter: (params) => {
        const brandName = compBrands[params.data[0]];
        const personaName = compPersonas[params.data[1]];
        const rate = params.data[2];
        const count = params.data[3];
        const total = params.data[4];
        return `<div class="font-sans">
          <div class="font-bold text-sm mb-1">${personaName} <span class="text-xs text-gray-500 font-normal">(样本量: ${total})</span></div>
          <div class="text-sm">对 <span class="font-semibold text-blue-500">${brandName}</span> 的推荐率为: <span class="font-bold">${rate}%</span></div>
          <div class="text-xs text-gray-500 mt-1">推荐提及: ${count} 次</div>
        </div>`;
      } 
    },
    grid: { left: '3%', right: '4%', height: '70%', top: '10%', containLabel: true },
    xAxis: { type: 'category', data: compBrands, splitArea: { show: true }, axisLabel: { color: textColor, interval: 0, rotate: 30 } },
    yAxis: { type: 'category', data: compPersonas, splitArea: { show: true }, axisLabel: { color: textColor, fontWeight: 'bold', interval: 0, width: 90, overflow: 'break' } },
    visualMap: {
      type: 'piecewise',
      dimension: 2,
      pieces: [
        { min: 80, max: 100, label: '80-100%', color: '#0284c7' },
        { min: 60, max: 79.9, label: '60-80%', color: '#0ea5e9' },
        { min: 40, max: 59.9, label: '40-60%', color: '#38bdf8' },
        { min: 20, max: 39.9, label: '20-40%', color: '#7dd3fc' },
        { min: 1, max: 19.9, label: '1-20%', color: '#bae6fd' },
        { min: 0, max: 0.9, label: '0%', color: isDark ? '#334155' : '#f1f5f9' }
      ],
      orient: 'horizontal',
      left: 'center',
      bottom: '0%',
      itemWidth: 14,
      itemHeight: 14,
      textStyle: { color: textColor, fontSize: 10 }
    },
    series: [{
      name: '品牌推荐率', type: 'heatmap', data: heatData,
      encode: { x: 0, y: 1, value: 2 },
      label: { show: true, formatter: (params) => `${params.data[2]}%` },
      emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.5)' } }
    }]
  };

  return (
    <div className="space-y-6 mt-4">
      
        {/* Brand Filter */}
        <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 flex items-center justify-between flex-wrap gap-4">
           <div className="flex flex-col gap-3 w-full">
               {/* Line 1: Pre-configured competitors */}
               <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-gray-700 dark:text-slate-300 whitespace-nowrap mt-1">核心设定竞品:</span>
                  <div className="flex flex-wrap gap-2">
                     {originalCompBrands.filter(b => !(data?.aiDiscoveredBrands || []).includes(b)).map(b => {
                        if (!b) return null;
                        const isSelected = selectedBrands.includes(b);
                        return (
                           <label key={b} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors border ${isSelected ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800/50 dark:text-blue-400' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-700'}`}>
                              <input 
                                 type="checkbox" 
                                 className="sr-only" 
                                 checked={isSelected}
                                 onChange={() => {
                                    if (isSelected && selectedBrands.length > 1) {
                                       setSelectedBrands(selectedBrands.filter(sb => sb !== b));
                                    } else if (!isSelected && selectedBrands.length < 8) {
                                       setSelectedBrands([...selectedBrands, b]);
                                    } else if (!isSelected && selectedBrands.length >= 8) {
                                       alert('为了保证图表可读性，最多只能同时选中 8 个品牌进行对比哦。');
                                    }
                                 }}
                              />
                              {isSelected && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                              {b}
                           </label>
                        )
                     })}
                  </div>
               </div>

               {/* Line 2: AI Discovered Brands */}
               {data?.aiDiscoveredBrands !== undefined && (
                  <div className="flex items-start gap-3 border-t border-gray-100 dark:border-slate-700/50 pt-3">
                     <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400 whitespace-nowrap mt-1 flex items-center gap-1">
                        <Sparkles className="w-4 h-4" />
                        AI 挖掘竞品:
                     </span>
                     <div className="flex flex-wrap gap-2">
                        {data?.aiDiscoveredBrands?.length === 0 && (
                           <span className="text-xs text-gray-400 dark:text-slate-500 mt-1">本周期内暂无新发现的竞品</span>
                        )}
                        {data?.aiDiscoveredBrands?.map(b => {
                           if (!b) return null;
                           const isSelected = selectedBrands.includes(b);
                           return (
                              <label key={b} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors border ${isSelected ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-800/50 dark:text-emerald-400' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-700'}`}>
                                 <input 
                                    type="checkbox" 
                                    className="sr-only" 
                                    checked={isSelected}
                                    onChange={() => {
                                       if (isSelected && selectedBrands.length > 1) {
                                          setSelectedBrands(selectedBrands.filter(sb => sb !== b));
                                       } else if (!isSelected && selectedBrands.length < 8) {
                                          setSelectedBrands([...selectedBrands, b]);
                                       } else if (!isSelected && selectedBrands.length >= 8) {
                                          alert('为了保证图表可读性，最多只能同时选中 8 个品牌进行对比哦。');
                                       }
                                    }}
                                 />
                                 {isSelected && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                                 {b}
                              </label>
                           )
                        })}
                     </div>
                  </div>
               )}
           </div>
           <div className="text-xs text-slate-400 mt-2">控制下方热力图的竞品显示数量</div>
        </div>

      {/* Top 2 Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sentiment Divergence */}
        <div className="bg-white dark:bg-slate-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center mb-4">
            <Activity className="w-5 h-5 text-emerald-500 mr-2" />
            品牌情感倾向分化图 (SOV & Sentiment)
          </h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
            AI 针对不同人设的提问，对我方品牌呈现的情感态度分歧。
          </p>
          <div className="h-[350px]">
            <ReactECharts option={sentimentOption} style={{ height: '100%', width: '100%' }} />
          </div>
        </div>

        {/* Competitor Interception */}
        <div className="bg-white dark:bg-slate-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center mb-4">
            <Target className="w-5 h-5 text-indigo-500 mr-2" />
            人设竞品拦截热力图 (Competitor Interception)
          </h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
            全域竞品在各个圈层下被 AI 提及与推荐的热度对比 (100% 为满额推荐)。
          </p>
          <div className="h-[350px]">
             {compBrands.length > 0 ? (
                <ReactECharts option={heatmapOption} style={{ height: '100%', width: '100%' }} />
             ) : (
                <div className="flex items-center justify-center h-full text-gray-400">暂无竞品数据</div>
             )}
          </div>
        </div>
      </div>

      {/* Semantic Tags Section */}
      <div className="bg-white dark:bg-slate-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center mb-4">
          <MessageSquare className="w-5 h-5 text-purple-500 mr-2" />
          人群差异化语料特征 (Semantic Footprint)
        </h3>
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">
          利用 Sapient Rank 实时抽取 AI 针对该圈层的核心种草场景与痛点标签。
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sentimentPersonas.map((persona, idx) => (
            <div key={idx} className="border border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <div className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#2996cf]" />
                  {persona}
                </div>
                {!tagsCache[persona] && !generatingTags[persona] && (
                  <button 
                    onClick={() => handleGenerateTags(persona)}
                    className="text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-3 py-1.5 rounded-full hover:bg-indigo-100 transition-colors flex items-center"
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    AI 抽取特征
                  </button>
                )}
              </div>
              
              {generatingTags[persona] ? (
                <div className="flex items-center justify-center py-6 text-indigo-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  <span className="text-sm">引擎萃取中...</span>
                </div>
              ) : tagsCache[persona] ? (
                <div className="flex flex-wrap gap-2">
                  {tagsCache[persona].map((tag, tIdx) => (
                    <span key={tIdx} className="bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs px-2.5 py-1.5 rounded-md border border-gray-200 dark:border-slate-700 shadow-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-400 dark:text-slate-500 py-4 text-center">
                  点击按钮一键生成人群画像标签
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
    </div>
  );
}
